import React from 'react';
import {
  MessageCircle, Tv, Star, CheckCircle, ArrowRight, Sparkles, Check
} from 'lucide-react';

export const DeliveryOptions = () => (
  <section className="py-20 md:py-28 bg-amber-50">
    <div className="max-w-6xl mx-auto px-8">
      <div className="text-center mb-16">
        <span className="text-amber-600 font-medium tracking-wide uppercase text-sm">Flexible Delivery</span>
        <h2 className="text-3xl md:text-4xl font-light text-stone-900 mt-4">
          Personalisation for every property type
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Budget Option */}
        <div className="bg-white p-10 rounded-3xl shadow-sm">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
            <MessageCircle className="w-8 h-8 text-green-600" />
          </div>
          <div className="text-sm text-green-600 font-medium mb-2">Budget-Friendly</div>
          <h3 className="text-2xl font-medium text-stone-900 mb-4">WhatsApp Personalisation</h3>
          <p className="text-stone-600 mb-6">
            Perfect for properties without smart room technology. Send heartfelt,
            personalized messages directly to your guest's phone.
          </p>
          <div className="bg-stone-50 rounded-xl p-4 text-sm text-stone-600 italic">
            "Dear Mr. Sharma, we remember you love our masala chai. Your favourite
            corner table by the garden is reserved for breakfast. Welcome back!"
          </div>
        </div>

        {/* Premium Option */}
        <div className="bg-white p-10 rounded-3xl shadow-sm">
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
            <Tv className="w-8 h-8 text-purple-600" />
          </div>
          <div className="text-sm text-purple-600 font-medium mb-2">Premium Experience</div>
          <h3 className="text-2xl font-medium text-stone-900 mb-4">Smart TV Welcome</h3>
          <p className="text-stone-600 mb-6">
            For hotels with in-room smart TVs. Greet guests with a personalized
            welcome screen the moment they enter their room.
          </p>
          <div className="bg-stone-900 rounded-xl p-4 text-white text-center">
            <div className="text-amber-400 text-sm mb-1">Welcome Back</div>
            <div className="text-2xl font-light">Mrs. Priya Mehta</div>
            <div className="text-stone-400 text-sm mt-2">Happy Anniversary! We've arranged roses in your room.</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const Stats = () => (
  <section className="py-20 md:py-28 bg-stone-900 text-white">
    <div className="max-w-6xl mx-auto px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {[
          { value: '40%', label: 'Increase in positive reviews' },
          { value: '3.2x', label: 'Higher guest retention' },
          { value: '25%', label: 'More direct bookings' },
          { value: '< 2 min', label: 'To generate Awe Story' }
        ].map((stat, i) => (
          <div key={i}>
            <div className="text-4xl md:text-5xl font-light text-amber-400 mb-2">{stat.value}</div>
            <div className="text-stone-400 text-sm">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Testimonial = () => (
  <section className="py-20 md:py-28 bg-white">
    <div className="max-w-4xl mx-auto px-8 text-center">
      <Star className="w-12 h-12 text-amber-400 mx-auto mb-8" />
      <blockquote className="text-2xl md:text-3xl font-light text-stone-900 leading-relaxed mb-8">
        "Anamaya transformed how we connect with guests. Our team now knows exactly
        what makes each guest tick, and our reviews have never been better."
      </blockquote>
      <div className="flex items-center justify-center gap-4">
        <div className="w-12 h-12 bg-stone-200 rounded-full"></div>
        <div className="text-left">
          <div className="font-medium text-stone-900">Bhashker Singh</div>
          <div className="text-sm text-stone-500">General Manager, The Golden Sunrise Hotel</div>
        </div>
      </div>
    </div>
  </section>
);

export const Pricing = () => (
  <section id="pricing" className="py-20 md:py-28 bg-white">
    <div className="max-w-6xl mx-auto px-8">
      <div className="text-center mb-16">
        <span className="text-amber-600 font-medium tracking-wide uppercase text-sm">Pricing</span>
        <h2 className="text-3xl md:text-5xl font-light text-stone-900 mt-4">
          Simple, transparent pricing
        </h2>
        <p className="text-lg text-stone-500 mt-4 max-w-2xl mx-auto">
          Choose the plan that fits your property. No hidden fees, no surprises.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        {/* Starter Plan */}
        <div className="bg-stone-50 rounded-3xl p-8 border border-stone-200">
          <div className="text-sm text-stone-500 font-medium mb-2">Starter</div>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-4xl font-light text-stone-900">₹30K</span>
            <span className="text-stone-500">/month</span>
          </div>
          <p className="text-stone-600 text-sm mb-6">
            Perfect for boutique hotels getting started with guest personalisation.
          </p>
          <ul className="space-y-3 mb-8">
            {[
              'Guest Preferences Directory',
              'AI Awe Story Generator',
              'Budget Controls',
              'Employee Leaderboard',
              'Hotel Rankings',
              'Basic Analytics',
              'Email Support'
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-stone-600">
                <Check className="w-4 h-4 text-amber-500 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
          <a
            href="#demo"
            className="block w-full text-center bg-stone-900 hover:bg-stone-800 text-white py-3 rounded-full font-medium transition-colors"
          >
            Get Started
          </a>
        </div>

        {/* Professional Plan */}
        <div className="bg-stone-900 rounded-3xl p-8 text-white relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-stone-900 text-xs font-bold px-4 py-1 rounded-full">
            MOST POPULAR
          </div>
          <div className="text-sm text-amber-400 font-medium mb-2">Professional</div>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-4xl font-light">₹45K</span>
            <span className="text-stone-400">/month</span>
          </div>
          <p className="text-stone-300 text-sm mb-6">
            For growing hotels that want the complete personalisation suite.
          </p>
          <ul className="space-y-3 mb-8">
            {[
              'Everything in Starter',
              'Social Media Auto-posting',
              'WhatsApp Personalisation',
              'Smart TV Greetings',
              'Advanced Analytics',
              'Multi-property Support',
              'API Integrations',
              'Priority Support'
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-stone-300">
                <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
          <a
            href="#demo"
            className="block w-full text-center bg-amber-500 hover:bg-amber-400 text-stone-900 py-3 rounded-full font-medium transition-colors"
          >
            Get Started
          </a>
        </div>

        {/* Enterprise Plan */}
        <div className="bg-stone-50 rounded-3xl p-8 border border-stone-200">
          <div className="text-sm text-stone-500 font-medium mb-2">Enterprise</div>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-4xl font-light text-stone-900">Custom</span>
          </div>
          <p className="text-stone-600 text-sm mb-6">
            For hotel chains and large properties with custom requirements.
          </p>
          <ul className="space-y-3 mb-8">
            {[
              'Everything in Professional',
              'Unlimited Properties',
              'Custom Integrations',
              'Dedicated Account Manager',
              'On-premise Deployment',
              'Custom AI Training',
              'SLA Guarantee',
              '24/7 Phone Support'
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-stone-600">
                <Check className="w-4 h-4 text-amber-500 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
          <a
            href="#demo"
            className="block w-full text-center bg-stone-900 hover:bg-stone-800 text-white py-3 rounded-full font-medium transition-colors"
          >
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  </section>
);

export const Founder = () => (
  <section className="py-20 md:py-28 bg-stone-50">
    <div className="max-w-5xl mx-auto px-8">
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
          <div className="w-48 h-48 md:w-64 md:h-64 rounded-2xl overflow-hidden flex-shrink-0 bg-stone-200">
            <img
              src="/assets/brochure/founder.jpg"
              alt="Nirvaan Krishnan"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-light text-stone-900 mb-6 leading-snug italic">
              'Hospitality is the art of anticipation and acknowledgement'
            </h3>
            <p className="text-stone-600 leading-relaxed mb-4">
              I spent years watching hotels collect feedback they never quite acted on.
              Reviews piled up, dashboards refreshed, but the next guest still arrived
              to the same generic welcome.
            </p>
            <p className="text-stone-600 leading-relaxed mb-6">
              Anamaya is the quiet solution to that. Less standardisation. More noticing.
              It is the layer that makes guests feel known — even at scale.
            </p>
            <div className="text-stone-900 font-medium">
              — Nirvaan Krishnan
              <span className="text-stone-500 font-normal ml-1">· Founder & CEO</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const DemoForm = ({ demoForm, setDemoForm, handleDemoSubmit, submitting }) => (
  <section id="demo" className="py-20 md:py-28 bg-white">
    <div className="max-w-6xl mx-auto px-8">
      <div className="grid md:grid-cols-2 gap-16 items-center">
        <div>
          <span className="text-amber-600 font-medium tracking-wide uppercase text-sm">Request Demo</span>
          <h2 className="text-3xl md:text-4xl font-light text-stone-900 mt-4 mb-6">
            See Anamaya in action
          </h2>
          <p className="text-lg text-stone-600 mb-8">
            Schedule a personalized demo with our team. We'll show you exactly how
            Anamaya can transform your guest experience.
          </p>

          <div className="space-y-6">
            {[
              { title: '30-minute walkthrough', desc: 'See all features in action' },
              { title: 'Custom use cases', desc: 'Tailored to your property type' },
              { title: 'ROI discussion', desc: 'Understand the business impact' },
              { title: 'Q&A session', desc: 'Get all your questions answered' }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-medium text-stone-900">{item.title}</div>
                  <div className="text-sm text-stone-500">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-stone-50 rounded-3xl p-8">
          <form onSubmit={handleDemoSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Your Name *</label>
                <input
                  type="text"
                  required
                  value={demoForm.name}
                  onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={demoForm.email}
                  onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="john@hotel.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Hotel / Property Name *</label>
              <input
                type="text"
                required
                value={demoForm.hotelName}
                onChange={(e) => setDemoForm({ ...demoForm, hotelName: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="The Grand Hotel"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={demoForm.phone}
                  onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Number of Rooms</label>
                <select
                  value={demoForm.rooms}
                  onChange={(e) => setDemoForm({ ...demoForm, rooms: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                >
                  <option value="">Select</option>
                  <option value="1-25">1 - 25 rooms</option>
                  <option value="26-50">26 - 50 rooms</option>
                  <option value="51-100">51 - 100 rooms</option>
                  <option value="101-200">101 - 200 rooms</option>
                  <option value="200+">200+ rooms</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Message (Optional)</label>
              <textarea
                value={demoForm.message}
                onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                placeholder="Tell us about your current challenges with guest personalisation..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-amber-500 hover:bg-amber-400 disabled:bg-amber-300 text-stone-900 py-4 rounded-full font-medium transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                'Submitting...'
              ) : (
                <>
                  Request Demo <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <p className="text-xs text-stone-500 text-center">
              By submitting, you agree to our Privacy Policy. We'll reach out within 24 hours.
            </p>
          </form>
        </div>
      </div>
    </div>
  </section>
);

export const CTA = () => (
  <section id="contact" className="py-20 md:py-28 bg-stone-900 text-white">
    <div className="max-w-4xl mx-auto px-8 text-center">
      <Sparkles className="w-16 h-16 text-amber-400 mx-auto mb-8" />
      <h2 className="text-3xl md:text-5xl font-light mb-6">
        Ready to create unforgettable<br />guest experiences?
      </h2>
      <p className="text-xl text-stone-300 mb-10 max-w-2xl mx-auto">
        Join the hotels that are leading the personalisation revolution.
        Schedule a demo and see Anamaya in action.
      </p>
      <div className="flex flex-wrap justify-center gap-4">
        <a href="#demo" className="bg-amber-500 hover:bg-amber-400 text-stone-900 px-10 py-4 rounded-full font-medium transition-colors flex items-center gap-2">
          Schedule Demo <ArrowRight className="w-5 h-5" />
        </a>
        <a href="/login" className="bg-white/10 hover:bg-white/20 backdrop-blur px-10 py-4 rounded-full font-medium transition-colors">
          Try Platform
        </a>
      </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="py-12 bg-stone-950 text-stone-400">
    <div className="max-w-6xl mx-auto px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-amber-400" />
          <span className="text-white text-xl font-light">Anamaya</span>
        </div>
        <div className="text-sm">
          Less standardisation, more personalisation with AI.
        </div>
        <div className="text-sm">
          &copy; 2025 Anamaya. All rights reserved.
        </div>
      </div>
    </div>
  </footer>
);
